/**
 * Shared helpers for locating agent-edited prose in the TipTap/PM document for temporary highlight.
 * Manuscript edits use markdown strings; the editor stores plain visible text — strip common markdown
 * so substring search can succeed.
 */

/**
 * Normalize smart quotes for substring matching (same idea as ProseMirrorBridge).
 * @param {string} s
 * @returns {string}
 */
export function normalizeQuotesForHighlightMatch(s) {
  return String(s)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"');
}

/**
 * @param {string} s
 * @returns {string}
 */
export function stripMarkdownForHighlightSnippet(s) {
  if (!s) return "";
  return String(s)
    .replace(/\r\n/g, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#+\s*/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Ordered candidates: try longer / more specific first, then shorter fallbacks for large inserts.
 * @param {string} rawSnippet
 * @returns {string[]}
 */
/** True when a highlight snippet likely refers to more than one manuscript paragraph. */
export function snippetSuggestsMultiParagraph(rawSnippet) {
  const plain = stripMarkdownForHighlightSnippet(String(rawSnippet ?? ""));
  return /\n\s*\n/.test(plain);
}

export function buildHighlightSearchCandidates(rawSnippet) {
  const trimmed = rawSnippet.trim();
  if (trimmed.length < 4) return [];

  const out = [];
  const seen = new Set();
  const push = (x) => {
    const t = typeof x === "string" ? x.trim() : "";
    if (t.length < 4) return;
    if (seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };

  push(trimmed);

  const plain = stripMarkdownForHighlightSnippet(trimmed);
  push(plain);

  const firstPara = (plain.split(/\n\s*\n/)[0] ?? plain).replace(/\n+/g, " ").trim();
  push(firstPara);

  const flat = plain.replace(/\s+/g, " ").trim();
  if (flat.length > 120) push(flat.slice(0, 120));
  else push(flat);

  const words = flat.split(/\s+/).filter(Boolean);
  if (words.length > 16) {
    push(words.slice(0, 16).join(" "));
  }

  return out;
}
